import { useState } from 'react'
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { projectBoardsService } from '../services/projectBoards.service'
import type { ProjectBoardDocument } from '@/types'

type Label = { id: string; name: string; color: string }

interface Props {
  board: ProjectBoardDocument & { id: string }
  selectedIds: string[]
  onChange: (selectedIds: string[]) => void
  onLabelsUpdated: () => void | Promise<void>
}

const COLOR_PALETTE = [
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#06b6d4',
  '#3b82f6',
  '#a855f7',
  '#ec4899',
]

export function BoardLabelChips({ board, selectedIds, onChange, onLabelsUpdated }: Props) {
  const labels: Label[] = board.meta?.labels ?? []
  const selectedLabels = labels.filter((l) => selectedIds.includes(l.id))

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState<string>(COLOR_PALETTE[0] ?? '#3b82f6')
  const [saving, setSaving] = useState(false)

  const persistLabels = async (nextLabels: Label[]) => {
    const nextMeta = {
      ...(board.meta ?? {}),
      labels: nextLabels,
    }
    await projectBoardsService.updateBoardMeta(board.id, nextMeta)
  }

  const toggleLabel = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id))
    } else {
      onChange([...selectedIds, id])
    }
  }

  const startRename = (label: Label) => {
    setEditingId(label.id)
    setEditingName(label.name)
  }

  const commitRename = async () => {
    if (!editingId) return
    const trimmed = editingName.trim()
    if (!trimmed) {
      setEditingId(null)
      return
    }
    setSaving(true)
    try {
      const next = labels.map((l) => (l.id === editingId ? { ...l, name: trimmed } : l))
      await persistLabels(next)
      setEditingId(null)
      setEditingName('')
      await onLabelsUpdated()
    } finally {
      setSaving(false)
    }
  }

  const deleteLabel = async (id: string) => {
    if (!window.confirm('Delete this label? It will be removed from all tasks that use it.')) {
      return
    }
    setSaving(true)
    try {
      const next = labels.filter((l) => l.id !== id)
      await persistLabels(next)
      if (selectedIds.includes(id)) {
        onChange(selectedIds.filter((x) => x !== id))
      }
      await onLabelsUpdated()
    } finally {
      setSaving(false)
    }
  }

  const addLabel = async () => {
    const trimmed = newName.trim()
    if (!trimmed) return
    setSaving(true)
    try {
      const newLabel: Label = {
        id: crypto.randomUUID(),
        name: trimmed,
        color: newColor,
      }
      const next = [...labels, newLabel]
      await persistLabels(next)
      setNewName('')
      setNewColor(COLOR_PALETTE[0] ?? '#3b82f6')
      await onLabelsUpdated()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex flex-wrap items-center gap-1 rounded px-1 py-0.5 text-left hover:bg-muted/50"
        >
          {selectedLabels.length === 0 ? (
            <span className="text-xs text-muted-foreground">+ Add label</span>
          ) : (
            selectedLabels.map((label) => (
              <span
                key={label.id}
                className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium text-white"
                style={{ backgroundColor: label.color }}
              >
                {label.name}
              </span>
            ))
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-3" align="start">
        <div className="space-y-3">
          <div className="text-xs font-semibold text-muted-foreground">Labels</div>
          <div className="max-h-60 space-y-1 overflow-y-auto">
            {labels.length === 0 && (
              <div className="text-xs text-muted-foreground">No labels yet.</div>
            )}
            {labels.map((label) => {
              const checked = selectedIds.includes(label.id)
              const isEditing = editingId === label.id
              return (
                <div key={label.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleLabel(label.id)}
                    className="h-4 w-4 shrink-0"
                    disabled={isEditing}
                  />
                  {isEditing ? (
                    <>
                      <Input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitRename()
                          if (e.key === 'Escape') setEditingId(null)
                        }}
                        className="h-7 flex-1 text-xs"
                        autoFocus
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={commitRename}
                        disabled={saving}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={() => setEditingId(null)}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <span
                        className="inline-flex flex-1 items-center rounded px-2 py-0.5 text-xs font-medium text-white"
                        style={{ backgroundColor: label.color }}
                      >
                        {label.name}
                      </span>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={() => startRename(label)}
                        disabled={saving}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={() => deleteLabel(label.id)}
                        disabled={saving}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              )
            })}
          </div>

          <div className="border-t pt-3">
            <div className="mb-2 text-xs font-semibold text-muted-foreground">New label</div>
            <div className="space-y-2">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addLabel()
                }}
                placeholder="Label name"
                className="h-7 text-xs"
              />
              <div className="flex flex-wrap gap-1">
                {COLOR_PALETTE.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setNewColor(color)}
                    className={`h-5 w-5 rounded ${
                      newColor === color ? 'ring-2 ring-offset-1 ring-foreground' : ''
                    }`}
                    style={{ backgroundColor: color }}
                    aria-label={`Color ${color}`}
                  />
                ))}
              </div>
              <Button
                type="button"
                size="sm"
                className="w-full"
                onClick={addLabel}
                disabled={saving || !newName.trim()}
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                Add
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
